import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { supabase, uploadPhotoToStorage } from "@/lib/supabase";
import { logActivity } from "@/lib/activityLog";
import PhoneInput from "@/components/base/PhoneInput";
import { marcar } from "@/lib/rastreio";

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

function normalizeUrl(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return "";
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

const INITIAL_FORM = {
  full_name: "",
  city_state: "",
  profession: "",
  description: "",
  country_code: "+1",
  whatsapp: "",
  email: "",
  instagram: "",
  website: "",
  bio_long: "",
  skills: "",
  languages: "",
  education: "",
  services: "",
  linkedin: "",
  facebook: "",
  tiktok: "",
  experience_years: "",
  business_name: "",
  business_address: "",
  business_city: "",
  business_state: "",
  business_zip: "",
  has_physical_business: false,
};

const socialChannels = [
  {
    name: "Instagram",
    handle: "@entreelas.community_",
    url: "https://instagram.com/entreelas.community_",
    icon: "ri-instagram-line",
    color: "bg-gradient-to-br from-amber-400 via-rose-400 to-amber-600",
  },
];

// Comprime a imagem no cliente: máx 800x800px, JPEG ~80%
function compressImage(file: File): Promise<{ blob: Blob; preview: string }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const MAX_DIM = 800;
      const QUALITY = 0.8;
      let { naturalWidth: w, naturalHeight: h } = img;
      if (w > MAX_DIM || h > MAX_DIM) {
        const ratio = Math.min(MAX_DIM / w, MAX_DIM / h);
        w = Math.round(w * ratio);
        h = Math.round(h * ratio);
      }

      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject(new Error("Falha ao processar a imagem no navegador."));
        return;
      }
      ctx.drawImage(img, 0, 0, w, h);

      // Gera preview (data URL) pra UI + Blob pro upload
      const preview = canvas.toDataURL("image/jpeg", QUALITY);
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error("Falha ao gerar a imagem comprimida."));
            return;
          }
          resolve({ blob, preview });
        },
        "image/jpeg",
        QUALITY,
      );
    };
    img.onerror = () => reject(new Error("Erro ao processar a imagem. Tente outra foto."));
    img.src = URL.createObjectURL(file);
  });
}

export default function JoinForm() {
  const [submitted, setSubmitted] = useState(false);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [photoError, setPhotoError] = useState("");
  const [submittedStatus, setSubmittedStatus] = useState<"criada" | "atualizada" | "ja_participa">("criada");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const photoFileRef = useRef<{ blob: Blob; preview: string } | null>(null);

  const [formData, setFormData] = useState({ ...INITIAL_FORM });
  const [emailError, setEmailError] = useState("");
  const [emailTouched, setEmailTouched] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    if (name === "email") {
      setEmailTouched(true);
      if (!value.trim()) {
        setEmailError("O email é obrigatório.");
      } else if (!isValidEmail(value)) {
        setEmailError("Insira um email válido (ex: nome@dominio.com).");
      } else {
        setEmailError("");
      }
    }
  };

  const handleEmailBlur = () => {
    setEmailTouched(true);
    if (!formData.email.trim()) {
      setEmailError("O email é obrigatório.");
    } else if (!isValidEmail(formData.email)) {
      setEmailError("Insira um email válido (ex: nome@dominio.com).");
    } else {
      setEmailError("");
    }
  };

  const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setPhotoError("A foto é muito grande (máx. 5MB). Escolha uma foto menor.");
      return;
    }

    setPhotoError("Comprimindo imagem...");

    try {
      const { blob, preview } = await compressImage(file);
      setPhotoPreview(preview);
      photoFileRef.current = { blob, preview };

      const sizeKb = Math.round(blob.size / 1024);
      setPhotoError(`Pronta! ${sizeKb}KB — será enviada para o servidor ao clicar em "Quero fazer parte".`);
    } catch (err) {
      setPhotoError((err as Error)?.message || "Erro ao processar a imagem.");
      setPhotoPreview(null);
      photoFileRef.current = null;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.email.trim()) {
      setEmailTouched(true);
      setEmailError("O email é obrigatório.");
      return;
    }
    if (!isValidEmail(formData.email)) {
      setEmailTouched(true);
      setEmailError("Insira um email válido (ex: nome@dominio.com).");
      return;
    }
    if (emailError) return;

    setSaving(true);
    setPhotoError("");

    const stored = photoFileRef.current;
    const rawDigits = formData.whatsapp.replace(/\D/g, "");

    let finalPhotoUrl: string | null = null;

    try {
      // Upload da foto direto pro Supabase Storage (zero edge function)
      if (stored?.blob) {
        setPhotoError("Enviando foto para o servidor...");
        const originalName = (fileInputRef.current?.files?.[0]?.name) || "foto.jpg";
        finalPhotoUrl = await uploadPhotoToStorage(stored.blob, originalName);
        setPhotoError("");
      }

      // Toda a lógica de criação/atualização/duplicidade agora vive na função RPC
      // join_community (servidor). O navegador não lê nem escreve a tabela members.
      const normalizedEmail = formData.email.trim().toLowerCase();

      const memberPayload = {
        full_name: formData.full_name.trim(),
        city_state: formData.city_state.trim(),
        profession: formData.profession.trim(),
        description: formData.description.trim(),
        whatsapp: rawDigits,
        country_code: formData.country_code,
        email: normalizedEmail,
        instagram: formData.instagram.trim() || null,
        website: normalizeUrl(formData.website) || null,
        bio_long: formData.bio_long.trim() || null,
        skills: formData.skills.trim() || null,
        languages: formData.languages.trim() || null,
        education: formData.education.trim() || null,
        services: formData.services.trim() || null,
        linkedin: normalizeUrl(formData.linkedin) || null,
        facebook: normalizeUrl(formData.facebook) || null,
        tiktok: formData.tiktok.trim() || null,
        experience_years: formData.experience_years.trim() || null,
        has_physical_business: formData.has_physical_business,
        business_name: formData.has_physical_business ? formData.business_name.trim() || null : null,
        business_address: formData.has_physical_business ? formData.business_address.trim() || null : null,
        business_city: formData.has_physical_business ? formData.business_city.trim() || null : null,
        business_state: formData.has_physical_business ? formData.business_state.trim() || null : null,
        business_zip: formData.has_physical_business ? formData.business_zip.trim() || null : null,
      };

      const { data: r, error: joinErr } = await supabase.rpc("join_community", {
        p_payload: { ...memberPayload, photo: finalPhotoUrl },
      });
      if (joinErr) throw new Error(joinErr.message);

      if (r?.status === "email_invalido") {
        setEmailTouched(true);
        setEmailError("Insira um email válido e tente novamente.");
        return;
      }

      if (formData.email) {
        localStorage.setItem("entre-elas-email", formData.email);
      }

      // Notificações disparam apenas quando o cadastro foi realmente criado.
      if (r?.status === "criada") {
        marcar("Lead");
        const approvalToken = r.approval_token;

        const rawPhone = formData.whatsapp.replace(/\D/g, "");
        const rawOrigin = window.location.origin;
        // Se estiver rodando em preview do Readdy (readdy.ai), usa o dominio publicado real
        const origin = rawOrigin.includes("readdy.ai")
          ? "https://entre-elas.readdy.co"
          : rawOrigin;
        const supabaseUrl = (import.meta.env.VITE_PUBLIC_SUPABASE_URL || "").replace(/\/$/, "");
        const anonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

        const adminPayload = JSON.stringify({
          name: formData.full_name,
          email: formData.email.trim(),
          city: formData.city_state,
          profession: formData.profession,
          whatsapp: rawPhone,
          countryCode: formData.country_code,
          status: "success",
          baseUrl: origin,
          approvalToken,
        });

        // notify-admin via fetch direto (controle total do body)
        fetch(`${supabaseUrl}/functions/v1/notify-admin`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${anonKey}`,
          },
          body: adminPayload,
        }).catch(() => {});

        supabase.functions.invoke("notify-whatsapp", {
          body: {
            name: formData.full_name,
            city: formData.city_state,
            profession: formData.profession,
            whatsapp: rawPhone,
            countryCode: formData.country_code,
            siteUrl: origin,
            token: approvalToken,
          },
        }).catch(() => {});

        logActivity("registration", {
          name: formData.full_name,
          profession: formData.profession,
          city: formData.city_state,
        });
      }

      setSubmittedStatus(
        r?.status === "ja_participa"
          ? "ja_participa"
          : r?.status === "atualizada"
            ? "atualizada"
            : "criada",
      );
      setSubmitted(true);
    } catch (err) {
      const msg = (err as Error)?.message || "Erro desconhecido";
      console.error("[JoinForm] Submit failed:", msg);
      setPhotoError(`Erro ao enviar: ${msg}`);
    } finally {
      setSaving(false);
    }
  };

  if (submitted) {
    return (
      <div className="w-full px-6 md:px-10 lg:px-16 max-w-2xl mx-auto text-center">
        <div className="bg-white rounded-2xl p-10 md:p-14">
          <div className="w-16 h-16 flex items-center justify-center rounded-full bg-primary/10 mx-auto mb-6">
            <i className="ri-check-line text-2xl text-primary" />
          </div>
          <h3 className="font-display text-2xl text-brown mb-3">
            {submittedStatus === "ja_participa"
              ? "Você já faz parte da comunidade!"
              : submittedStatus === "atualizada"
                ? "Cadastro atualizado!"
                : "Cadastro enviado!"}
          </h3>
          {submittedStatus === "ja_participa" ? (
            <p className="font-body text-brown/70 text-base leading-relaxed mb-6">
              Você já faz parte da Entre Elas. Para editar seu perfil, use o link que
              você recebeu por e-mail, ou fale com a Ana.
            </p>
          ) : submittedStatus === "atualizada" ? (
            <p className="font-body text-brown/70 text-base leading-relaxed mb-6">
              Recebemos sua atualização. Seu cadastro continua em análise.
            </p>
          ) : (
            <>
              <p className="font-body text-brown/70 text-base leading-relaxed mb-2">
                Seu cadastro foi recebido e está <strong>em análise</strong>.
              </p>
              <p className="font-body text-brown/60 text-sm leading-relaxed mb-6">
                Assim que a Ana Karina aprovar, seu perfil aparecerá automaticamente na comunidade.
                Fique de olho na sua caixa de entrada!
              </p>
            </>
          )}
          <div className="mb-6 pt-6 border-t border-nude">
            <p className="font-display text-lg text-brown mb-1 leading-snug">
              Uma rede se constrói juntas.
            </p>
            <p className="font-body text-sm text-brown/60 mb-5 leading-relaxed">
              Siga o Entre Elas e faça parte dessa corrente de mulheres que se apoiam, crescem e transformam umas às outras.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {socialChannels.map((channel) => (
                <a
                  key={channel.name}
                  href={channel.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`inline-flex items-center gap-2 px-6 py-3 rounded-full ${channel.color} text-white font-body text-sm tracking-wide transition-all duration-300 hover:scale-105 hover:shadow-lg whitespace-nowrap cursor-pointer`}
                >
                  <i className={`${channel.icon} text-lg`} />
                  Seguir no {channel.name}
                </a>
              ))}
            </div>
            <p className="font-body text-xs text-brown/40 mt-3">
              {socialChannels[0].handle}
            </p>
          </div>

          <Link
            to="/"
            className="inline-block font-body text-sm px-8 py-3 rounded-full bg-primary text-white tracking-wide uppercase transition-all duration-300 hover:bg-primary-dark whitespace-nowrap"
          >
            Voltar para o início
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full px-6 md:px-10 lg:px-16 max-w-2xl mx-auto">
      <div className="text-center mb-10 md:mb-14">
        <p className="font-body text-xs tracking-[0.25em] uppercase text-primary mb-3">
          Junte-se a Nós
        </p>
        <h2 className="font-display text-2xl md:text-3xl lg:text-4xl text-brown mb-4">
          Cadastre-se na comunidade
        </h2>
        <p className="font-body text-brown/70 text-base leading-relaxed max-w-xl mx-auto">
          Preencha o formulário abaixo para fazer parte da nossa rede de
          apoio. Após a aprovação, você poderá completar seu perfil com mais detalhes.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-6 md:p-10 space-y-5" noValidate>
          {saving && (
            <div className="px-4 py-3 rounded-lg bg-yellow-50 border border-yellow-200 text-yellow-700 font-body text-sm flex items-center gap-2">
              <i className="ri-loader-4-line animate-spin" />
              Enviando cadastro...
            </div>
          )}
          {photoError && !saving && (
            <div className={`px-4 py-3 rounded-lg font-body text-sm leading-relaxed ${
              photoError.startsWith("Pronta!")
                ? "bg-green-50 border border-green-200 text-green-700"
                : "bg-red-50 border border-red-200 text-red-700"
            }`}>
              {photoError}
            </div>
          )}

          <div className="flex flex-col items-center gap-3 pb-5 border-b border-nude">
            <label className="font-body text-sm text-brown block mb-1">
              Foto de perfil <span className="text-primary">*</span>
            </label>
            <div
              onClick={() => fileInputRef.current?.click()}
              className="w-32 h-32 rounded-full bg-beige flex items-center justify-center cursor-pointer overflow-hidden border-2 border-dashed border-nude hover:border-primary transition-colors"
            >
              {photoPreview ? (
                <img src={photoPreview} alt="Preview" className="w-full h-full object-cover" />
              ) : (
                <div className="flex flex-col items-center text-brown/40">
                  <i className="ri-camera-line text-2xl mb-1" />
                  <span className="text-xs">Adicionar foto</span>
                </div>
              )}
            </div>
            <input ref={fileInputRef} type="file" accept="image/*" onChange={handlePhotoChange} className="hidden" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className="font-body text-sm text-brown block mb-2">
                Nome completo <span className="text-primary">*</span>
              </label>
              <input
                type="text" name="full_name" required
                value={formData.full_name} onChange={handleChange}
                className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
                placeholder="Seu nome completo"
              />
            </div>
            <div>
              <label className="font-body text-sm text-brown block mb-2">
                Cidade / Estado <span className="text-primary">*</span>
              </label>
              <input
                type="text" name="city_state" required
                value={formData.city_state} onChange={handleChange}
                className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
                placeholder="Boca Raton, FL"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className="font-body text-sm text-brown block mb-2">
                Profissão <span className="text-primary">*</span>
              </label>
              <input
                type="text" name="profession" required
                value={formData.profession} onChange={handleChange}
                className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
                placeholder="Sua profissão"
              />
            </div>
            <div>
              <label className="font-body text-sm text-brown block mb-2">
                WhatsApp <span className="text-primary">*</span>
              </label>
              <PhoneInput
                countryCode={formData.country_code}
                whatsapp={formData.whatsapp}
                onChange={({ countryCode, whatsapp }) =>
                  setFormData({ ...formData, country_code: countryCode, whatsapp })
                }
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className="font-body text-sm text-brown block mb-2">
                Email <span className="text-primary">*</span>
              </label>
              <input
                type="email"
                name="email"
                required
                value={formData.email}
                onChange={handleChange}
                onBlur={handleEmailBlur}
                className={`w-full px-4 py-3 rounded-lg border bg-beige/50 font-body text-sm placeholder-brown/40 focus:outline-none transition-colors ${
                  emailTouched && emailError
                    ? "border-red-400 text-red-700 focus:border-red-500"
                    : "border-nude text-brown focus:border-primary"
                }`}
                placeholder="seu@email.com"
              />
              {emailTouched && emailError && (
                <p className="font-body text-xs text-red-500 mt-1.5 flex items-center gap-1">
                  <i className="ri-error-warning-line" />
                  {emailError}
                </p>
              )}
            </div>
            <div>
              <label className="font-body text-sm text-brown block mb-2">
                Instagram
              </label>
              <input
                type="text" name="instagram"
                value={formData.instagram} onChange={handleChange}
                className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
                placeholder="@seuinstagram"
              />
            </div>
          </div>

          <div>
            <label className="font-body text-sm text-brown block mb-2">
              Site (opcional)
            </label>
            <input
              type="text" name="website"
              value={formData.website} onChange={handleChange}
              className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
              placeholder="seusite.com"
            />
          </div>

          <div>
            <label className="font-body text-sm text-brown block mb-2">
              Resumo sobre você <span className="text-primary">*</span>
            </label>
            <textarea
              name="description" required maxLength={300}
              value={formData.description} onChange={handleChange}
              rows={3}
              className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors resize-none"
              placeholder="Conte um pouco sobre você, seus interesses e o que te move..."
            />
            <p className="font-body text-xs text-brown/50 mt-1 text-right">{formData.description.length}/300</p>
          </div>

          {/* ─── Perfil Detalhado ─── */}
          <div className="border-t border-nude pt-5">
            <p className="font-display text-base text-brown mb-4">
              Perfil detalhado
            </p>

            <div>
              <label className="font-body text-sm text-brown block mb-2">
                Bio completa
              </label>
              <textarea
                name="bio_long" maxLength={1000}
                value={formData.bio_long} onChange={handleChange}
                rows={4}
                className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors resize-none"
                placeholder="Conte sua história, sua trajetória, o que te inspira e o que você busca..."
              />
              <p className="font-body text-xs text-brown/50 mt-1 text-right">{formData.bio_long.length}/1000</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="font-body text-sm text-brown block mb-2">
                  Habilidades / Especialidades
                </label>
                <input
                  type="text" name="skills"
                  value={formData.skills} onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
                  placeholder="Marketing, Coaching, Design..."
                />
                <p className="font-body text-xs text-brown/40 mt-1">Separe por vírgulas</p>
              </div>
              <div>
                <label className="font-body text-sm text-brown block mb-2">
                  Idiomas
                </label>
                <input
                  type="text" name="languages"
                  value={formData.languages} onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
                  placeholder="Português, Inglês, Espanhol..."
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="font-body text-sm text-brown block mb-2">
                  Formação / Certificações
                </label>
                <input
                  type="text" name="education"
                  value={formData.education} onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
                  placeholder="Graduação, pós, certificações..."
                />
              </div>
              <div>
                <label className="font-body text-sm text-brown block mb-2">
                  Anos de experiência
                </label>
                <input
                  type="text" name="experience_years"
                  value={formData.experience_years} onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
                  placeholder="5+ anos"
                />
              </div>
            </div>

            <div>
              <label className="font-body text-sm text-brown block mb-2">
                Serviços que oferece
              </label>
              <textarea
                name="services" maxLength={500}
                value={formData.services} onChange={handleChange}
                rows={3}
                className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors resize-none"
                placeholder="Descreva os serviços que você oferece..."
              />
              <p className="font-body text-xs text-brown/50 mt-1 text-right">{formData.services.length}/500</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="font-body text-sm text-brown block mb-2">
                  LinkedIn
                </label>
                <input
                  type="text" name="linkedin"
                  value={formData.linkedin} onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
                  placeholder="linkedin.com/in/seuperfil"
                />
              </div>
              <div>
                <label className="font-body text-sm text-brown block mb-2">
                  Facebook
                </label>
                <input
                  type="text" name="facebook"
                  value={formData.facebook} onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
                  placeholder="facebook.com/seuperfil"
                />
              </div>
            </div>

            <div>
              <label className="font-body text-sm text-brown block mb-2">
                TikTok
              </label>
              <input
                type="text" name="tiktok"
                value={formData.tiktok} onChange={handleChange}
                className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
                placeholder="@seutiktok"
              />
            </div>
          </div>

          {/* ─── Negócio com endereço físico ─── */}
          <div className="border-t border-nude pt-5">
            <label className="flex items-start gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={formData.has_physical_business}
                onChange={(e) => setFormData({ ...formData, has_physical_business: e.target.checked })}
                className="mt-0.5 w-4 h-4 accent-primary cursor-pointer"
              />
              <span className="font-body text-sm text-brown">
                Tenho um negócio com endereço físico
              </span>
            </label>

            {formData.has_physical_business && (
              <div className="mt-4 space-y-4">
                <div>
                  <label className="font-body text-sm text-brown block mb-2">
                    Nome do negócio
                  </label>
                  <input
                    type="text" name="business_name"
                    value={formData.business_name} onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
                    placeholder="Nome do seu negócio"
                  />
                </div>
                <div>
                  <label className="font-body text-sm text-brown block mb-2">
                    Endereço
                  </label>
                  <input
                    type="text" name="business_address"
                    value={formData.business_address} onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
                    placeholder="Rua, número, complemento"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                  <div>
                    <label className="font-body text-sm text-brown block mb-2">Cidade</label>
                    <input
                      type="text" name="business_city"
                      value={formData.business_city} onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
                      placeholder="Cidade"
                    />
                  </div>
                  <div>
                    <label className="font-body text-sm text-brown block mb-2">Estado</label>
                    <input
                      type="text" name="business_state"
                      value={formData.business_state} onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
                      placeholder="FL"
                    />
                  </div>
                  <div>
                    <label className="font-body text-sm text-brown block mb-2">ZIP</label>
                    <input
                      type="text" name="business_zip"
                      value={formData.business_zip} onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
                      placeholder="CEP / ZIP"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="pt-4">
            <button
              type="submit"
              disabled={saving}
              className="w-full font-body text-sm px-10 py-4 rounded-full bg-primary text-white tracking-wide uppercase transition-all duration-300 hover:bg-primary-dark disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
            >
              {saving ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin" />
                  {photoError.includes("Enviando foto") ? "Enviando foto..." : "Enviando..."}
                </span>
              ) : (
                "Quero fazer parte da comunidade"
              )}
            </button>
          </div>
        </form>
    </div>
  );
}